import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './NewsSearchModal.module.scss';
import useIsRtl from '../../../../../app/helpers/isRtl';
import Text from '../../../../atoms/text/Text';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { useTranslation } from 'react-i18next';
import getCookie from '../../../../../app/helpers/getCookie';
function CalendarModal({ open, onClose, value, onChange, minDate, dir }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(open);
  const [phase, setPhase] = useState(open ? 'enter' : 'idle');
  const exitMs = 280;

  useEffect(() => {
    if (open) {
      setVisible(true);
      const id = requestAnimationFrame(() => setPhase('enter'));
      return () => cancelAnimationFrame(id);
    } else if (visible) {
      setPhase('exit');
      const t = setTimeout(() => {
        setVisible(false);
        setPhase('idle');
      }, exitMs);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!visible) return null;

  return (
    <div
      className={[
        styles.calendarOverlay,
        phase === 'enter' && styles.calendarOpen,
        phase === 'exit' && styles.calendarClosing,
      ]
        .filter(Boolean)
        .join(' ')}
      role="dialog"
      aria-modal="true"
      dir={dir}
      onClick={(e) => e.target === e.currentTarget && onClose?.('cancel')}
    >
      <div
        className={styles.calendarCard}
        onClick={(e) => e.stopPropagation()}
        role="document"
        dir="ltr"
      >
        <DateCalendar value={value} onChange={onChange} minDate={minDate} />
        <div className={styles.calendarActions}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => onClose?.('clear')}
          >
            <ClearIcon fontSize="small" />
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => onClose?.('apply')}
          >
            <DoneIcon fontSize="small" />
          </button>
        </div>
      </div>
    </div>
  );
}
export default function NewsSearchModal({
  isOpen,
  onClose,
  containerRefs,
  containerRef,
  onFiltered,
}) {
  const isEnglish = getCookie('googtrans')?.includes('/ar/en');

  const { t } = useTranslation();
  const isRtl = useIsRtl();
  const modalRef = useRef(null);
  const inputRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  const [query, setQuery] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState(null);
  const [pendingEnd, setPendingEnd] = useState(null);

  useEffect(() => setMounted(true), []);

  const refs = useMemo(() => {
    if (Array.isArray(containerRefs)) return containerRefs.filter(Boolean);
    return containerRef ? [containerRef] : [];
  }, [containerRefs, containerRef]);

  const getAllSearchNodes = useCallback(() => {
    const all = [];
    refs.forEach((r) => {
      const root = r?.current;
      if (!root) return;
      all.push(...root.querySelectorAll('[data-search-item]'));
    });
    return all;
  }, [refs]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(id);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const resetHidden = useCallback(() => {
    const nodes = getAllSearchNodes();
    nodes.forEach((n) => n.classList.remove(styles.hidden));
  }, [getAllSearchNodes]);

  const applyFilters = useCallback(() => {
    const q = (query || '').toLowerCase();
    const startTs = start ? new Date(start + 'T00:00:00').getTime() : NaN;
    const endTs = end ? new Date(end + 'T23:59:59').getTime() : NaN;

    const nodes = getAllSearchNodes();
    nodes.forEach((node) => {
      const textOk = q ? node.innerText.toLowerCase().includes(q) : true;
      const raw = node.getAttribute('data-date') || '';
      const ts = new Date(raw).getTime();

      let dateOk = true;
      if (start || end) {
        if (Number.isNaN(ts)) dateOk = false;
        else {
          if (!Number.isNaN(startTs) && ts < startTs) dateOk = false;
          if (!Number.isNaN(endTs) && ts > endTs) dateOk = false;
        }
      }

      node.classList.toggle(styles.hidden, !(textOk && dateOk));
    });

    const refsArr = Array.isArray(containerRefs)
      ? containerRefs
      : [containerRef];
    const counts = refsArr.filter(Boolean).map((r) => {
      const root = r?.current;
      if (!root) return 0;
      const all = root.querySelectorAll('[data-search-item]');
      let visible = 0;
      all.forEach((n) => {
        if (!n.classList.contains(styles.hidden)) visible++;
      });
      return visible;
    });

    const total = counts.reduce((a, b) => a + b, 0);
    const headerTotal = counts[0] ?? 0;
    const listTotal = counts[counts.length - 1] ?? total;
    onFiltered?.({ total, headerTotal, listTotal });
  }, [
    query,
    start,
    end,
    getAllSearchNodes,
    containerRefs,
    containerRef,
    onFiltered,
  ]);
  const clearAll = useCallback(() => {
    setQuery('');
    setStart('');
    setEnd('');
    resetHidden();
    onFiltered?.(null);
  }, [resetHidden, onFiltered]);

  const onSubmit = (e) => {
    e.preventDefault();
    applyFilters();
    onClose?.();
  };
  if (!mounted) return null;

  const overlay = (
    <div
      aria-hidden={!isOpen}
      className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className={`${styles.sheet} ${isOpen ? styles.enter : styles.leave}`}
      >
        <div
          className={styles['header']}
          style={{ flexDirection: !isRtl ? 'row-reverse' : '' }}
        >
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>

          <Text
            title={isEnglish ? 'Search' : 'common.search'}
            variant="text-italic-bold-30"
            style={{ textTransform: 'capitalize' }}
          />
        </div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <form className={styles.searchBar} onSubmit={onSubmit}>
            <div className={styles.row} dir={isRtl ? 'rtl' : ''}>
              <div className={styles.field}>
                <Text title="common.startDate" variant="text-italic-bold-30" />
                <button
                  type="button"
                  className={styles.dateButton}
                  onClick={() => {
                    setStartOpen(true);
                  }}
                  style={{ textAlign: isRtl ? 'right' : 'left' }}
                >
                  <span
                    className={styles.text}
                    style={{ textAlign: isRtl ? 'right' : '' }}
                  >
                    {start || t('common.pickDate')}
                  </span>
                </button>
                <CalendarModal
                  open={startOpen}
                  dir={isRtl ? 'rtl' : 'ltr'}
                  value={start ? dayjs(start) : null}
                  onChange={(val) =>
                    setStart(val ? val.format('YYYY-MM-DD') : '')
                  }
                  onClose={() => setStartOpen(false)}
                />
              </div>

              <div className={styles.field}>
                <Text title="common.endDate" variant="text-italic-bold-30" />

                <button
                  type="button"
                  className={styles.dateButton}
                  onClick={() => {
                    setEndOpen(true);
                  }}
                  style={{ textAlign: isRtl ? 'right' : 'left' }}
                >
                  <span
                    className={styles.text}
                    style={{ textAlign: isRtl ? 'right' : '' }}
                  >
                    {end || t('common.pickDate')}
                  </span>
                </button>
                <CalendarModal
                  open={endOpen}
                  dir={isRtl ? 'rtl' : 'ltr'}
                  value={end ? dayjs(end) : null}
                  minDate={start ? dayjs(start) : undefined}
                  onChange={(val) =>
                    setEnd(val ? val.format('YYYY-MM-DD') : '')
                  }
                  onClose={() => setEndOpen(false)}
                />
              </div>
            </div>

            <label className={styles.field} style={{ marginTop: 20 }}>
              <Text
                title={isEnglish ? 'Search' : 'common.search'}
                variant="text-italic-bold-30"
              />

              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('common.typeToSearch')}
                aria-label="Search input"
                style={{ textAlign: isRtl ? 'right' : 'left' }}
                dir={isRtl ? 'rtl' : ''}
              />
            </label>

            <div className={styles.actions} dir={isRtl ? 'rtl' : 'ltr'}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={clearAll}
              >
                <Text
                  title={isEnglish ? 'Clear' : 'common.clear'}
                  variant="text-italic-bold-30"
                  className={styles.buttonText}
                />
              </button>
              <button type="submit" className={styles.primaryBtn}>
                <Text
                  title={isEnglish ? 'Search' : 'common.search'}
                  variant="text-italic-bold-30"
                  className={styles.buttonText}
                />
              </button>
            </div>
          </form>
        </LocalizationProvider>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
